import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Platform, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Textarea } from '@/src/elements/input/Textarea'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useDelayedFn } from '@/src/general/hooks/useDelayedFn'
import { isMobileBrowser } from '@/src/general/utils/isMobileBrowser'
import type { TWithBrowserTextareaHeightProps } from '@/src/general/utils/withBrowserTextareaHeight'
import { useTranslate } from '@/src/lang/useTranslate'

import type { TMessageTextareaProps } from './MessageTextarea.types'

export const MessageTextarea = ({
  ref,
  isLoading,
  onSubmitMessage,
  onSubmitPrayer,
  onFocus,
  onBlur,
  onLayout,
  onValueUpdate,
}: TMessageTextareaProps & Partial<TWithBrowserTextareaHeightProps>) => {
  const [initialContentHeight, setInitialContentHeight] = useState<number>(0)
  const [content, setContent] = useState<string>('')
  const { t } = useTranslate()
  const showSubmitButton = !!content.length

  const updateContent = (newContent: string) => {
    setContent(newContent)
    onValueUpdate?.()
  }
  // We delay onBlur to have enough time to check if the submit button was pressed
  // without the delay the onBlur event will be called before the submit
  // and the keyboard will be closed
  const delayedOnBlur = useDelayedFn(onBlur, 150)

  const onSubmit = async () => {
    const normalizedContent = content.trim()
    if (!normalizedContent || !onSubmitMessage) return
    try {
      await onSubmitMessage(normalizedContent)
      updateContent('')
    } catch {
      // Keep draft when submit is rejected (e.g. offline).
    }
  }

  return (
    <View className="flex flex-row gap-2">
      <Textarea
        className="h-auto min-h-12 flex-1 rounded-2xl shadow-md h-fit"
        ref={ref}
        value={content}
        placeholder={t('words.your-message')}
        numberOfLines={1}
        textAlignVertical="center"
        style={Platform.OS === 'ios' ? { paddingTop: 0, paddingBottom: 0 } : undefined}
        // Disabled for mobile to be able to create new lines
        blurOnSubmit={!isMobileBrowser()}
        onFocus={onFocus}
        onBlur={delayedOnBlur}
        maxHeight={initialContentHeight * 4}
        onContentSizeChange={(e) => {
          const height = e.nativeEvent.contentSize.height
          if (initialContentHeight) return
          setInitialContentHeight(height)
        }}
        onChange={(event) => updateContent(event.nativeEvent.text)}
        onLayout={onLayout}
        onKeyPress={(e) => {
          // @ts-expect-error - missing type adaptation shiftKey
          const isShiftKeyPressed = !!e.nativeEvent.shiftKey
          const isLongEnough = content.length >= 1
          if (e.nativeEvent.key === 'Enter' && !isShiftKeyPressed && !isMobileBrowser() && isLongEnough) {
            onSubmit()
          }
        }}
        testID={TEST_ID.MESSAGES.TEXT_INPUT}
        maxLength={1000}
      />
      {/* Distinct keys force a remount when switching buttons: otherwise React reuses the same native view
      and on iOS the submit button styles (e.g. aspect-square) are not reset, keeping the pray button square */}
      {showSubmitButton ? (
        <Button
          key="submit"
          isDisabled={!onSubmitMessage}
          isLoading={isLoading}
          variant="solid"
          colors="primary"
          size="md"
          testID={TEST_ID.MESSAGES.SUBMIT_BUTTON}
          onPress={async () => {
            await onSubmit()
            onBlur?.()
          }}
          isIconOnly
          className="aspect-square !h-auto !w-auto !min-w-0 self-stretch"
          startIcon={ICON.SEND}
        />
      ) : (
        <Button
          key="pray"
          testID={TEST_ID.MESSAGES.PRAY_BUTTON}
          isDisabled={!onSubmitPrayer}
          isLoading={isLoading}
          variant="solid"
          colors="primary"
          size="md"
          onPress={onSubmitPrayer}
          startIcon={ICON.FIRE}
        >
          <FormattedMessage id="pray.i-pray.short" />
        </Button>
      )}
    </View>
  )
}
